import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Check, CheckCircle, KeyRound, Loader,
  LockKeyhole, RefreshCw, Save, ShieldCheck, Trash2, X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';

const DEFAULT_VIEWPORT = { width: 1280, height: 720 };
const BRIDGE_SOURCE = 'testsuite-ai-app';
const EXTENSION_SOURCE = 'testsuite-ai-extension';

const isHttpUrl = (value = '') => {
  try {
    const url = new URL(String(value || '').trim());
    return ['http:', 'https:'].includes(url.protocol) && Boolean(url.host);
  } catch (_error) { return false; }
};

const formatCapturedAt = (value = '') => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return 'Not captured';
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const resolveApiError = async (response, fallback) => {
  const payload = await response.json().catch(() => ({}));
  return (typeof payload?.detail === 'string' && payload.detail.trim())
    || (typeof payload?.message === 'string' && payload.message.trim()) || fallback;
};

const sendExtensionBridgeMessage = (message, timeoutMs = 12000) => new Promise((resolve, reject) => {
  const requestId = `auth-capture-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const cleanup = () => {
    window.clearTimeout(timerId);
    window.removeEventListener('message', handleMessage);
  };
  const handleMessage = (event) => {
    const data = event.data || {};
    if (event.source !== window || data.source !== EXTENSION_SOURCE || data.requestId !== requestId) return;
    cleanup();
    if (!data.ok) reject(new Error(data.error || 'Chrome extension could not capture login.'));
    else resolve(data.response || data);
  };
  const timerId = window.setTimeout(() => {
    cleanup();
    reject(new Error('Feature Capture extension is unavailable. Reload it, refresh TestSuite, and try again.'));
  }, timeoutMs);
  window.addEventListener('message', handleMessage);
  window.postMessage({ source: BRIDGE_SOURCE, requestId, ...message }, window.location.origin);
});

const SetupStep = ({ number, title, copy, active, complete }) => (
  <div className="flex items-start gap-4">
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-bold ${complete
      ? 'border-cyan-600 bg-cyan-600 text-white'
      : active ? 'border-blue-600 bg-blue-600 text-white ring-4 ring-blue-100' : 'border-slate-300 bg-white text-slate-500'}`}>
      {complete ? <Check className="h-4 w-4" /> : number}
    </span>
    <div className="min-w-0 pt-0.5">
      <p className={`text-sm font-semibold ${active ? 'text-blue-800' : complete ? 'text-slate-800' : 'text-slate-500'}`}>{title}</p>
      <p className="mt-1 text-sm leading-6 text-slate-500">{copy}</p>
    </div>
  </div>
);

const AutomationLoginSkeleton = () => (
  <div className="motion-safe:animate-pulse" aria-label="Loading automation login" role="status">
    <span className="sr-only">Loading automation login</span>
    <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span className="h-9 w-9 shrink-0 rounded-md bg-slate-200" />
        <div className="space-y-2">
          <span className="block h-3.5 w-32 rounded bg-slate-200" />
          <span className="block h-2.5 w-24 rounded bg-slate-200/80" />
        </div>
      </div>
      <span className="h-7 w-24 rounded-md bg-slate-200" />
    </div>

    <div className="mt-5">
      <span className="block h-3.5 w-24 rounded bg-slate-200" />
      <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3.5 py-3">
        <span className="block h-3.5 w-3/5 rounded bg-slate-200" />
        <span className="mt-2 block h-2.5 w-full rounded bg-slate-200/80" />
        <span className="mt-2 block h-2.5 w-4/5 rounded bg-slate-200/80" />
      </div>
    </div>

    <div className="mt-5 flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-4">
      <span className="h-5 w-5 shrink-0 rounded bg-slate-200" />
      <div className="w-full space-y-2">
        <span className="block h-3.5 w-48 rounded bg-slate-200" />
        <span className="block h-2.5 w-full rounded bg-slate-200/80" />
        <span className="block h-2.5 w-3/4 rounded bg-slate-200/80" />
      </div>
    </div>
  </div>
);

export const AutomationEnvironmentModal = ({
  pdfId, defaultLoginUrl = '', onClose, onSaved, onContinue, onAuthStateChange,
}) => {
  const [authState, setAuthState] = useState(null);
  const [authLoading, setAuthLoading] = useState(Boolean(pdfId));
  const [authError, setAuthError] = useState('');
  const loginUrl = String(defaultLoginUrl || '').trim();
  const [captureTabId, setCaptureTabId] = useState(0);
  const [captureStarting, setCaptureStarting] = useState(false);
  const [captureSaving, setCaptureSaving] = useState(false);
  const [authRefreshing, setAuthRefreshing] = useState(false);
  const captureTabRef = useRef(0);

  const metadata = authState?.metadata || {};
  const sessionAvailable = Boolean(authState?.available);
  const replayAvailable = Boolean(metadata.login_replay_available);
  const loginReady = sessionAvailable || replayAvailable;
  const captureActive = Boolean(captureTabId);
  const busy = captureStarting || captureSaving || authRefreshing;
  const guideStep = loginReady && !captureActive ? 4 : captureSaving ? 3 : captureActive ? 2 : 1;

  useEffect(() => { captureTabRef.current = captureTabId; }, [captureTabId]);

  const loadAuthState = useCallback(async () => {
    if (!pdfId) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await apiFetch(API_ENDPOINTS.PLAYWRIGHT_AUTH_STATE(pdfId));
      if (!response.ok) throw new Error(await resolveApiError(response, 'Failed to load login state'));
      setAuthState(await response.json().catch(() => ({})));
    } catch (error) { setAuthError(error?.message || 'Failed to load login state'); }
    finally { setAuthLoading(false); }
  }, [pdfId]);

  useEffect(() => { loadAuthState(); }, [loadAuthState]);
  useEffect(() => {
    const handleExtensionSave = (event) => {
      const data = event.data || {};
      if (event.source !== window || data.source !== EXTENSION_SOURCE || data.type !== 'lsq-auth-capture-saved') return;
      if (String(data.pdfId || '') !== String(pdfId || '')) return;
      const payload = data.result || {};
      setCaptureTabId(0);
      setAuthError('');
      setAuthState(payload);
      onAuthStateChange?.(payload);
      onSaved?.(payload);
    };
    window.addEventListener('message', handleExtensionSave);
    return () => window.removeEventListener('message', handleExtensionSave);
  }, [onAuthStateChange, onSaved, pdfId]);
  useEffect(() => () => {
    if (captureTabRef.current) {
      sendExtensionBridgeMessage({ type: 'lsq-auth-capture-cancel', captureTabId: captureTabRef.current }).catch(() => null);
    }
  }, []);

  const startCapture = async () => {
    const resolvedUrl = String(loginUrl || '').trim();
    setAuthError('');
    if (!isHttpUrl(resolvedUrl)) { setAuthError('Configure a valid HTTP or HTTPS Application URL first.'); return; }
    try {
      setCaptureStarting(true);
      const result = await sendExtensionBridgeMessage({
        type: 'lsq-auth-capture-start', endpoint: API_BASE_URL || '', pdfId, loginUrl: resolvedUrl,
      }, 30000);
      if (result?.ok === false) throw new Error(result.error || 'Could not open login capture.');
      const tabId = Number(result?.tabId || 0);
      if (!tabId) throw new Error('The extension did not open a login window.');
      setCaptureTabId(tabId);
    } catch (error) { setAuthError(error?.message || 'Could not open login capture.'); }
    finally { setCaptureStarting(false); }
  };

  const saveCapture = async () => {
    if (!captureTabId) return;
    try {
      setCaptureSaving(true);
      setAuthError('');
      const payload = await sendExtensionBridgeMessage({ type: 'lsq-auth-capture-save', captureTabId }, 45000);
      setCaptureTabId(0);
      setAuthState(payload);
      onAuthStateChange?.(payload);
      onSaved?.(payload);
    } catch (error) { setAuthError(error?.message || 'Failed to save login from the extension.'); }
    finally { setCaptureSaving(false); }
  };

  const cancelCapture = async () => {
    const tabId = captureTabId;
    setCaptureTabId(0);
    if (tabId) await sendExtensionBridgeMessage({ type: 'lsq-auth-capture-cancel', captureTabId: tabId }).catch(() => null);
  };

  const refreshAuthState = async () => {
    if (!pdfId || captureActive || !replayAvailable) return;
    try {
      setAuthRefreshing(true);
      setAuthError('');
      const response = await apiFetch(API_ENDPOINTS.PLAYWRIGHT_AUTH_STATE_REFRESH(pdfId), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ viewport: DEFAULT_VIEWPORT }),
      });
      if (!response.ok) throw new Error(await resolveApiError(response, 'Failed to refresh login state'));
      const payload = await response.json().catch(() => ({}));
      setAuthState(payload);
      onAuthStateChange?.(payload);
    } catch (error) { setAuthError(error?.message || 'Failed to refresh login state'); }
    finally { setAuthRefreshing(false); }
  };

  const deleteAuthState = async () => {
    if (!pdfId || busy) return;
    try {
      setAuthLoading(true);
      setAuthError('');
      const response = await apiFetch(API_ENDPOINTS.PLAYWRIGHT_AUTH_STATE(pdfId), { method: 'DELETE' });
      if (!response.ok) throw new Error(await resolveApiError(response, 'Failed to delete login state'));
      const payload = await response.json().catch(() => ({}));
      setAuthState(payload);
      onAuthStateChange?.(payload);
    } catch (error) { setAuthError(error?.message || 'Failed to delete login state'); }
    finally { setAuthLoading(false); }
  };

  const closeModal = () => { cancelCapture(); onClose(); };

  return (
    <div className="modal-overlay p-3 sm:p-6">
      <div className="modal-shell flex max-h-[88dvh] w-[min(680px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_24px_72px_rgba(15,23,42,0.22)]">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-600 text-white"><ShieldCheck className="h-5 w-5" /></span>
            <div><h2 className="text-lg font-semibold text-slate-950">Automation login</h2><p className="mt-0.5 text-sm text-slate-500">Used for feature capture and automated test runs</p></div>
          </div>
          <button type="button" onClick={closeModal} className="icon-btn h-9 w-9 shrink-0 rounded-md" aria-label="Close"><X className="h-4 w-4" /></button>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {authLoading && !authState ? <AutomationLoginSkeleton /> : <>
          <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${loginReady ? 'bg-cyan-100 text-cyan-700' : captureActive ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>{authLoading ? <Loader className="h-4 w-4 animate-spin" /> : loginReady ? <CheckCircle className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}</span>
              <div><p className="text-sm font-semibold text-slate-900">{loginReady ? sessionAvailable && replayAvailable ? 'Fast login ready' : sessionAvailable ? 'Saved session ready' : 'Login script ready' : captureActive ? 'Private login window is open' : 'Login setup required'}</p><p className="mt-0.5 text-xs text-slate-500">{loginReady ? `Saved ${formatCapturedAt(metadata.captured_at)}` : captureActive ? 'Keep it open until the extension confirms the login is saved' : 'Capture the login once for this environment'}</p></div>
            </div>
            {loginReady && <span className="shrink-0 rounded-md border border-cyan-200 bg-white px-2 py-1 text-xs font-semibold text-cyan-800">{sessionAvailable ? 'Session first' : 'Auto login'}</span>}
          </div>

          <div className="mt-5">
            <label className="mb-2 block text-sm font-semibold text-slate-900">Application URL</label>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3.5 py-3">
              <p className="truncate text-sm font-medium text-slate-900" title={loginUrl}>{loginUrl || 'Not configured'}</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Managed by this application environment. Use the login page when authentication is required, or the home page when it is not.</p>
            </div>
          </div>

          {authError && <div className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{authError}</span></div>}

          {loginReady && !captureActive ? (
            <div className="mt-5 flex items-start gap-3 rounded-md border border-cyan-200 bg-cyan-50 px-4 py-3.5">
              <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-cyan-700" />
              <div><p className="text-sm font-semibold text-slate-900">{sessionAvailable && replayAvailable ? 'Fast session with automatic fallback' : replayAvailable ? 'Login script is ready' : 'Saved session is ready'}</p><p className="mt-1 text-sm leading-6 text-slate-600">{sessionAvailable && replayAvailable ? 'Feature capture restores this session first. If it has expired, the saved login script runs automatically.' : replayAvailable ? 'Feature capture performs this login before recording.' : 'Feature capture restores this session before recording. Recapture the login to add automatic fallback when it expires.'}</p></div>
            </div>
          ) : (
            <div className="mt-6 space-y-5 border-l border-slate-200 pl-5">
              <SetupStep number={1} title="Open the login window" copy="The extension opens a clean private Chrome session." active={guideStep === 1} complete={guideStep > 1} />
              <SetupStep number={2} title="Sign in and keep the window open" copy="Complete credentials and verification, then wait for the application to load. Do not close the private window." active={guideStep === 2} complete={guideStep > 2} />
              <SetupStep number={3} title="Save from the extension" copy="Select “Save Login & Return to TestSuite.” The extension saves the session, closes the private window, and brings you back here." active={guideStep === 3} complete={guideStep > 3} />
            </div>
          )}
          </>}
        </main>

        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          {authLoading && !authState ? (
            <div className="flex items-center gap-2 motion-safe:animate-pulse" aria-hidden="true">
              <span className="h-10 w-28 rounded-md bg-slate-200" />
              <span className="h-10 w-24 rounded-md bg-slate-200" />
            </div>
          ) : <div className="flex flex-wrap items-center justify-end gap-2">
            {captureActive ? <>
              <button type="button" onClick={cancelCapture} disabled={captureSaving} className="secondary-btn h-10 rounded-md px-4 text-sm">Cancel</button>
              <button type="button" onClick={saveCapture} disabled={captureSaving} className="secondary-btn h-10 rounded-md px-4 text-sm">{captureSaving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{captureSaving ? 'Saving...' : 'Save login from here'}</button>
            </> : loginReady ? <>
              <button type="button" onClick={deleteAuthState} disabled={busy} className="inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold text-slate-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"><Trash2 className="h-4 w-4" />Clear saved login</button>
              {replayAvailable && <button type="button" onClick={refreshAuthState} disabled={busy} title="Test the saved automatic login" className="secondary-btn h-10 rounded-md px-3 text-sm disabled:opacity-50">{authRefreshing ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{authRefreshing ? 'Testing...' : 'Test script'}</button>}
              <button type="button" onClick={startCapture} disabled={!pdfId || busy || !loginUrl} className="secondary-btn h-10 rounded-md px-3 text-sm"><KeyRound className="h-4 w-4" />Recapture</button>
              {onContinue && <button type="button" onClick={onContinue} disabled={busy} className="primary-btn h-10 rounded-md bg-blue-600 px-4 text-sm hover:bg-blue-700 disabled:bg-slate-300">Continue</button>}
            </> : <button type="button" onClick={startCapture} disabled={!pdfId || busy || !loginUrl} className="primary-btn h-10 rounded-md bg-blue-600 px-4 text-sm hover:bg-blue-700 disabled:bg-slate-300">{captureStarting ? <Loader className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}{captureStarting ? 'Opening...' : 'Start login capture'}</button>}
          </div>}
        </footer>
      </div>
    </div>
  );
};
